// IndicatorsContext.ts
import React from 'react';
import { GoalType } from '../utils/apiRoutes';

// Matches WordPress API prepare_indicator() response
export interface Indicator {
  id: number;
  title: string;
  title_render: string;
  goal_id: number | null;
  target_id: number | null;
  // New optional visualization and helper fields
  chartSeriesDescription?: string;
  unit?: string | null;
  yAxisMin?: number | null;
  yAxisMax?: number | null;
  moeEnabled?: boolean;
  moeMode?: 'around' | 'up' | 'down' | string;
  moePercent?: number | null;
  acf: {
    [key: string]: any; // ACF fields can vary
    index?: string;
    long_term_objective?: string;
    target_value?: string;
    has_numeric_target?: boolean;
    status?: string | number;
    source?: string;
    graph_data_indicators?: {
      data_display_type?: string;
      whole_data?: string;
      suffix?: string;
      plain_text?: {
        plain_text_label?: string;
        value?: string;
      };
      pie_chart?: Array<{
        label?: string;
        value?: number;
        color?: string | null;
        cutout?: string;
      }>;
    };
    reference_field?: string;
    select_targets?: number[];
    indicator_type?: string;
  };
  computed?: {
    score?: number | null;
    trend?: string | number | null;
    trend_score?: number | null;
    scores_by_series?: {
      [key: string]: {
        score?: number;
        semaphore?: string | number;
        trend?: string | number;
        [key: string]: any;
      };
    };
    semaphore_status?: string | number;
    status_score?: number | null;
    trend_status?: string | number;
    baseline?: {
      year?: number | null;
      value?: number | null;
    } | null;
    latest?: {
      year?: number | null;
      value?: number | null;
    } | null;
    target?: {
      year?: number | null;
      value?: number | null;
    } | null;
    eu_status_details?: Record<string, unknown> | null;
    /** D-26 / Pietro Jul 18 Q5–Q7, Q12 — availability / relevance of Kosovo series */
    eu_data_status?: string | null;
    availability_message?: string | null;
    kosovo_vs_eu_percent?: number | null;
  };
  chartType?: 'bar' | 'line' | null;
  chartData?: any;
  /** EU multi-unit indicators: one chart per Eurostat unit */
  chartGraphs?: Array<{
    chartType?: 'bar' | 'line' | null;
    chartData?: any;
    seriesDescription?: string;
    unitLabel?: string | null;
    /** Per-series axis/tooltip unit; overrides indicator.unit for this chart */
    unit?: string | null;
    eurostatUnitCode?: string | null;
    isAssessmentSeries?: boolean;
    isBarChart?: boolean;
  }>;
  /** Indicator type from backend: quantitative, ordinal, or binary (yes/no) */
  indicatorType?: 'quantitative' | 'ordinal' | 'binary';
  /** EU-only editorial tag (D-2): none | national | proxy */
  indicatorTag?: 'none' | 'national' | 'proxy';
  featured_image_url?: string;
}

interface IndicatorsContextType {
  indicators: Indicator[];
  loading: boolean;
  initialLoading: boolean;
  fetchAllIndicators: () => Promise<void>;
  fetchAllIndicatorsByType: (goalType: GoalType) => Promise<void>;
  fetchAllIndicatorsByGoal: (goalId: number, goalType: GoalType) => Promise<void>;
  fetchAllIndicatorsByTarget: (targetId: number, goalType: GoalType) => Promise<void>;
  fetchSingleIndicator: (indicatorId: number, goalType: GoalType) => Promise<Indicator | null>;
}

const defaultContextValue: IndicatorsContextType = {
  indicators: [],
  loading: false,
  initialLoading: true,
  fetchAllIndicators: async () => {},
  fetchAllIndicatorsByType: async () => {},
  fetchAllIndicatorsByGoal: async () => {},
  fetchAllIndicatorsByTarget: async () => {},
  fetchSingleIndicator: async () => null,
};


const IndicatorsContext = React.createContext<IndicatorsContextType>(defaultContextValue);

export { IndicatorsContext };